import {
  ArrowBackIcon,
  ArrowForwardIcon,
  CheckIcon,
  SmallCloseIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Text,
  // Icon,
  useRadio,
  useRadioGroup,
  VStack,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import * as React from "react";
import {
  PAID_LESSON_FEE_STATUS,
  PAID_SPEAKING_FEE_STATUS,
} from "../stores/jobs/utils/request";
import Icon from "../components/ui-elements/Icon";
import LoadingDots from "./data-display/LoadingDots";

const contactClientTips = [
  {
    icon: "guarantee",
    title: "Be professional and friendly",
    description:
      "Create a strong first impression with the client. Start by introducing yourself as a Tuteria tutor and appreciate them for their interest in hiring you. It's a good practice to ask for the best time to call or to send a message ahead before calling. We'll show you a beautifully-crafted message you can send in 1-click.",
    question: "What does this mean?",
    answer: "optionB",
    options: [
      {
        label: "Call the client at anytime even if it's inconvenient for them",
        value: "optionA",
      },
      {
        label:
          "Send a message to introduce yourself, then call at a good time afterwards",
        value: "optionB",
      },
      {
        label: "Call the client and let them know you are a professional",
        value: "optionC",
      },
    ],
  },
  {
    icon: "special-needs",
    title: "Show an interest in your student",
    description:
      "Clients hire tutors to help make a difference. So endeavour to ask the right questions about the learner to help you better understand their specific needs; it also shows that you care. Where possible, seek to speak with the child too. It'll help you better prepare for the first lesson.",
    question: "What does this mean?",
    answer: "optionC",
    options: [
      {
        label:
          "The client's needs are already stated in the lesson details we provided so no need to call.",
        value: "optionA",
      },
      {
        label:
          "Don't ask questions on the call since you can do so when lessons start.",
        value: "optionB",
      },
      {
        label:
          "Seek to understand the client's goals and the learner's needs by asking the right questions.",
        value: "optionC",
      },
    ],
  },
  {
    icon: "interview",
    title: "Respond to questions confidently",
    description:
      "Most times, these initial calls help clients to determine if you are the best fit for them. Be calm and confident when responding to questions they may ask about your experience, background or how you can help. See it as an opportunity to demonstrate how awesome you are!",
    question: "What does this mean?",
    answer: "optionA",
    options: [
      {
        label:
          "Be prepared to calmly and honestly respond to any clarifying questions clients may have about you",
        value: "optionA",
      },
      {
        label:
          "Since the client is interested in hiring you, only answer a few questions and you're good to go.",
        value: "optionB",
      },
      {
        label:
          "No need to prepare for questions clients may ask, you may respond to them anyhow.",
        value: "optionC",
      },
    ],
  },
  {
    icon: "calendar-checked",
    title: "Try to agree on a start date",
    description:
      "Don't just end the call. Confirm the lesson details you've received from us such as client's location, subjects, schedule, duration etc. Also try to reach a conclusion on a specific date when lessons should commence. This is needed when booking lessons.",
    question: "What does this mean?",
    answer: "optionC",
    options: [
      {
        label:
          "Don't discuss start date or schedule or subjects with clients, leave that to Tuteria",
        value: "optionA",
      },
      {
        label:
          "If the client is not yet sure about when to start, then you pick a start date for them.",
        value: "optionB",
      },
      {
        label:
          "Clarify any lesson information and ask the client when they would like lessons to commence",
        value: "optionC",
      },
    ],
  },
];

const getClientContactTips = (requestStatus: string) => {
  const paidSpeakingFeeTip = {
    icon: "notification",
    title: "Tell them what happens next",
    description:
      "Let them know what to expect. In this particular instance, the client has NOT yet paid the lesson fee so you cannot commence lessons. Let the the client know that you'll send feedback of the call to us, and that Tuteria will be in touch to book lessons so that you can get started.",
    question: "What does this mean?",
    answer: "optionB",
    options: [
      {
        label:
          "If clients request for you to commence lessons, you can do so even without a confirmed booking from us.",
        value: "optionA",
      },
      {
        label:
          "Inform clients that Tuteria will be in touch after the call to book lessons so that you can get started.",
        value: "optionB",
      },
      {
        label:
          "Negotiate with clients and seek to get them to pay an amount higher than your hourly rate.",
        value: "optionC",
      },
    ],
  };

  const paidLessonFeeTip = {
    icon: "notification",
    title: "Tell them what happens next",
    description:
      "Let them know what to expect. In this instance, the client has already paid for the lessons so we can proceed if you have a start date. Let the client know that you'll submit feedback about the call, and also book their lessons on Tuteria so that you can get started.",
    question: "What does this mean?",
    answer: "optionB",
    options: [
      {
        label:
          "Since the client has already made payment, you can go ahead and commence lessons.",
        value: "optionA",
      },
      {
        label:
          "Inform clients that lessons will be booked shortly to enable you commence on the agreed start date",
        value: "optionB",
      },
      {
        label:
          "Negotiate with clients and seek to get them to pay an amount higher than your hourly rate.",
        value: "optionC",
      },
    ],
  };

  if (requestStatus === PAID_SPEAKING_FEE_STATUS) {
    return [...contactClientTips, paidSpeakingFeeTip];
  }

  if (requestStatus === PAID_LESSON_FEE_STATUS) {
    return [...contactClientTips, paidLessonFeeTip];
  }

  return contactClientTips;
};

const CustomRadio = React.forwardRef((props: any, ref: any) => {
  const {
    isChecked,
    isDisabled,
    value,
    isSelected,
    isInvalid,
    children,
    ...rest
  } = props;
  const { getCheckboxProps, getInputProps } = useRadio(props);
  const checkbox: any = getCheckboxProps();
  const input: any = getInputProps();
  return (
    <Box as="label">
      <input {...input} />
      <Box {...checkbox} cursor="pointer">
        <Stack
          isInline
          align="baseline"
          ref={ref}
          aria-checked={isChecked}
          role="radio"
          isDisabled={isDisabled}
          {...rest}
        >
          <Box minW={6} minH={6}>
            {!isSelected ? (
              <Radio size="lg" value={value} />
            ) : (
              <Icon
                size={6}
                ml="-2px"
                mt={-1}
                name={isInvalid ? "small-close" : "check"}
                color={isInvalid ? "red.500" : "green.500"}
              />
            )}
          </Box>
          {!isSelected ? (
            <Text>{children}</Text>
          ) : (
            <Text color={isInvalid ? "red.500" : "green.500"}>{children}</Text>
          )}
        </Stack>
      </Box>
    </Box>
  );
});

// const CustomRadio = React.forwardRef<any, any>((props, ref) => {
//   const {
//     isChecked,
//     isDisabled,
//     value,
//     isSelected,
//     isInvalid,
//     children,
//     ...rest
//   } = props;

//   const { getCheckboxProps, getInputProps } = useRadio(props);
//   const checkbox = getCheckboxProps();
//   const input = getInputProps();
//   console.log(checkbox);
//   return (
//     <Stack
//       isInline
//       align="baseline"
//       ref={ref}
//       aria-checked={isChecked}
//       role="radio"
//       isDisabled={isDisabled}
//       as="label"
//       {...rest}
//       {...checkbox}
//       cursor="pointer"
//     >
//       <input {...input} />
//       <Box minW={6} minH={6}>
//         {!isSelected ? (
//           <Radio size="lg" value={value} />
//         ) : (
//           <Icon
//             as={isInvalid ? SmallCloseIcon : CheckIcon}
//             size={6}
//             ml="-2px"
//             mt={-1}
//             color={isInvalid ? "red.500" : "green.500"}
//           />
//         )}
//       </Box>
//       {!isSelected ? (
//         <Text>{children}</Text>
//       ) : (
//         <Text color={isInvalid ? "red.500" : "green.500"}>{children}</Text>
//       )}
//     </Stack>
//   );
// });

type Props = {
  onSubmit: (...args: any[]) => Promise<any>;
  onClose: () => void;
  requestStatus: any;
};

const ContactClientTips = observer(
  ({
    onSubmit = async () => {},
    onClose = () => {},
    requestStatus,
    ...rest
  }: Props) => {
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [answers, setAnswers] = React.useState<any[]>([]);
    const [completed, setCompleted] = React.useState(false);
    const [isInvalid, setIsInvalid] = React.useState(false);
    const [error, setError] = React.useState(false);
    const contactClientTips = getClientContactTips(requestStatus);
    const currentTip = contactClientTips[currentIndex];
    const isLastIndex = currentIndex + 1 === contactClientTips.length;
    const { getRootProps, getRadioProps } = useRadioGroup({
      name: "tips",
      onChange: (value: any) => handleChange(value),
    });
    const group: any = getRootProps();

    const handleChange = (answerOption: any) => {
      const isCorrectOption = answerOption === currentTip.answer;
      if (answers.length < currentIndex + 1) {
        setAnswers([...answers, answerOption]);
      } else {
        const answersArr = [...answers];
        answersArr[currentIndex] = answerOption;
        setAnswers(answersArr);
      }

      if (isCorrectOption) {
        setError(false);
        setIsInvalid(false);
      } else setIsInvalid(true);
    };

    const handleBackClick = () => {
      if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
    };

    const handleNextClick = () => {
      if (!isLastIndex) {
        if (answers.length >= currentIndex + 1) {
          if (!isInvalid) {
            setCurrentIndex(currentIndex + 1);
            setIsInvalid(false);
            setError(false);
          } else setError(true);
        } else setError(true);
      }
      if (isLastIndex && !completed) {
        if (answers.length === currentIndex + 1) {
          if (!isInvalid) {
            setCompleted(true);
            onSubmit({}, false)
              .then(() => {
                // setError(false);
                onClose();
              })
              .catch((error) => {
                setError(error);
              });
          } else {
            setError(true);
          }
        } else {
          setError(true);
        }
      }
    };

    if (completed) {
      return (
        <Flex
          direction="column"
          h="100%"
          justifyContent="center"
          align="center"
        >
          <Stack py={8} spacing={6} textAlign="center" align="center">
            <Icon name="thumbs-up" size={20} color="green.500" />
            <Stack align="center">
              <Text fontSize="xl" fontWeight="bold">
                Thank you!
              </Text>
              <Text fontSize="lg">Retrieving client's contact</Text>
            </Stack>

            <LoadingDots />
          </Stack>
        </Flex>
      );
    }

    return (
      <Flex
        direction="column"
        justifyContent="space-between"
        minHeight="100%"
        {...rest}
      >
        <Stack spacing={5}>
          <Icon name={currentTip.icon} color="green.500" size={[10, 12]} />

          <Stack mt={3}>
            <Text fontWeight="bold" fontSize="lg">
              {currentIndex + 1}. {currentTip.title}
            </Text>
            <Text>{currentTip.description}</Text>
          </Stack>

          <FormControl isInvalid={error}>
            <FormLabel mb={2}>{currentTip.question}</FormLabel>
            {/* <VStack spacing={4} {...group}>
              {currentTip.options.map(({ label, value }, index) => {
                const radio = getRadioProps({ value });
                return (
                  <CustomRadio
                    key={index}
                    isSelected={answers[currentIndex] === value}
                    isInvalid={isInvalid}
                    {...radio}
                  >
                    {label}
                  </CustomRadio>
                );
              })}
            </VStack> */}
            <RadioGroup
              key={currentIndex}
              // onChange={handleChange}
              value={answers[currentIndex]}
            >
              <VStack spacing={4} {...group}>
                {currentTip.options.map((option, index) => {
                  const radio = getRadioProps({ value: option.value } as any);
                  return (
                    <CustomRadio
                      key={index}
                      isSelected={answers[currentIndex] === option.value}
                      isInvalid={isInvalid}
                      value={option.value}
                      {...radio}
                    >
                      {option.label}
                    </CustomRadio>
                  );
                })}
              </VStack>
            </RadioGroup>
            <FormErrorMessage>Please select correct option</FormErrorMessage>
          </FormControl>
        </Stack>

        <Flex justifyContent="space-between" pt={6} align="center">
          {currentIndex > 0 ? (
            <Button
              leftIcon={<ArrowBackIcon />}
              variant="ghost"
              onClick={handleBackClick}
            >
              Previous
            </Button>
          ) : (
            <Box mr={8} />
          )}

          <Text fontSize={["xs", "sm"]} opacity={0.8}>
            {currentIndex + 1} of {contactClientTips.length}
          </Text>

          <Button
            rightIcon={<ArrowForwardIcon />}
            colorScheme="blue"
            onClick={handleNextClick}
          >
            {isLastIndex ? "Done" : "Next"}
          </Button>
        </Flex>
      </Flex>
    );
  },
);

export default ContactClientTips;
